import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import { CompanyId, SiteId } from '../common/ids';
import { ManagedBySchema } from '../common/integration';

// ---------------------------------------------------------------------------
// Enums (data-model.md · sites)
// ---------------------------------------------------------------------------

export const SiteStatus = z.enum(['ACTIVE', 'INACTIVE']);
export type SiteStatus = z.infer<typeof SiteStatus>;

// ---------------------------------------------------------------------------
// Campos (rangos de data-model.md)
// ---------------------------------------------------------------------------

export const LatitudeSchema = z.number().min(-90).max(90);
export const LongitudeSchema = z.number().min(-180).max(180);
export const SITE_RADIUS_DEFAULT_M = 150;
export const RadiusMetersSchema = z.number().int().min(20).max(2000);

const siteEditableFields = {
  name: z.string().min(1).max(80),
  description: z.string().max(300).nullable(),
  address: z.string().min(1).max(200),
  timezone: TimezoneSchema,
  latitude: LatitudeSchema,
  longitude: LongitudeSchema,
  radius_m: RadiusMetersSchema,
};

// ---------------------------------------------------------------------------
// Entidad
// ---------------------------------------------------------------------------

export const SiteSchema = z.object({
  id: SiteId,
  company_id: CompanyId,
  ...siteEditableFields,
  status: SiteStatus,
  deactivated_at: NullableIsoDateTimeSchema,
  /**
   * 009 · vínculo con el sistema externo: `fields` son los campos de solo lectura (R-14) y la API
   * responde `409 INTEGRATION_MANAGED` si se intenta cambiarlos. `null` = sede solo de Fermin.
   * Obligatorio desde US6 (T053, `sites/mapper.ts`).
   */
  managed_by: ManagedBySchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});
export type Site = z.infer<typeof SiteSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * `POST /sites`: `{ name, description?, address, timezone?, latitude, longitude, radius_m? }`.
 * `timezone` por defecto = `companies.default_timezone` (lo resuelve la API);
 * `radius_m` por defecto 150.
 */
export const CreateSiteBodySchema = z.object({
  name: siteEditableFields.name,
  description: siteEditableFields.description.optional(),
  address: siteEditableFields.address,
  timezone: TimezoneSchema.optional(),
  latitude: LatitudeSchema,
  longitude: LongitudeSchema,
  radius_m: RadiusMetersSchema.default(SITE_RADIUS_DEFAULT_M),
});
export type CreateSiteBody = z.infer<typeof CreateSiteBodySchema>;

/** `PATCH /sites/{id}`: parcial de los campos editables. */
export const UpdateSiteBodySchema = z.object(siteEditableFields).partial();
export type UpdateSiteBody = z.infer<typeof UpdateSiteBodySchema>;

/** `GET /sites?status&q` (sin paginación; filtrado por `site_scope` si aplica). */
export const SiteListQuerySchema = z.object({
  status: SiteStatus.optional(),
  q: z.string().min(1).max(80).optional(),
});
export type SiteListQuery = z.infer<typeof SiteListQuerySchema>;

/** Parámetro de ruta `{id}` de sede. */
export const SiteIdParamsSchema = z.object({ id: SiteId });
export type SiteIdParams = z.infer<typeof SiteIdParamsSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 { items: Site[] }` */
export const SiteListResponseSchema = z.object({
  items: z.array(SiteSchema),
});
export type SiteListResponse = z.infer<typeof SiteListResponseSchema>;
